import { Observable, fromEvent, merge } from "rxjs";
import {
    distinctUntilChanged,
    endWith,
    filter,
    map,
    repeat,
    switchMap,
    takeUntil,
} from "rxjs/operators";

export const LEFT_RIGHT = "left-right";
export const RIGHT_LEFT = "right-left";
export type DirectionType = typeof LEFT_RIGHT | typeof RIGHT_LEFT;

export interface SwipeObserverOptions {
    target: HTMLElement;
    enableMouseEvent: boolean;
    tolerance: number;
    direction: DirectionType;
}

export class SwipeObserver {
    private start$: Observable<MouseEvent>;
    private move$: Observable<MouseEvent>;
    private end$: Observable<MouseEvent>;

    constructor(private options: SwipeObserverOptions) {
        // start events
        const mouseDown$ = fromEvent<MouseEvent>(options.target, "mousedown");
        const touchStart$ = fromEvent<MouseEvent>(options.target, "touchstart");
        this.start$ = options.enableMouseEvent
            ? merge(mouseDown$, touchStart$)
            : merge(touchStart$);
        // move events
        const mouseMove$ = fromEvent<MouseEvent>(options.target, "mousemove");
        const touchMove$ = fromEvent<MouseEvent>(options.target, "touchmove");
        this.move$ = options.enableMouseEvent
            ? merge(mouseMove$, touchMove$)
            : merge(touchMove$);
        // end events
        const mouseUp$ = fromEvent<MouseEvent>(options.target, "mouseup");
        const touchEnd$ = fromEvent<MouseEvent>(options.target, "touchend");
        const mouseLeave$ = fromEvent<MouseEvent>(options.target, "mouseleave");
        this.end$ = options.enableMouseEvent
            ? merge(mouseUp$, touchEnd$, mouseLeave$)
            : merge(touchEnd$, mouseLeave$);
    }

    start(): {
        shift$: Observable<number>;
        swipe$: Observable<number>;
    } {
        // сдвигом мы считаем расстояние в пикселях между коогдинатами, где мы нажали на мышь(mousedown)
        // и координатой куда мы сдвинули мышь(mousemove)
        // если произойдет событие $end, то мы кинем ноль и заново подпишемся на исходные потоки
        const shift$ = this.start$.pipe(
            switchMap((startEvent) =>
                this.move$.pipe(
                    map((event) => event.pageX),
                    distinctUntilChanged(),
                    filter((pageX) => {
                        switch (this.options.direction) {
                            case LEFT_RIGHT:
                                return pageX > startEvent.pageX;
                            case RIGHT_LEFT:
                                return pageX < startEvent.pageX;
                        }
                    }),
                    map((pageX) => {
                        return Math.round(pageX - startEvent.pageX);
                    }),
                    takeUntil(this.end$),
                    endWith(0)
                )
            )
        );
        // подписываемся на сдвиг и если он больше чем какое-то число,
        // то кидаем сообдение в этот поток, о том, что произошел свайп
        const swipe$ = shift$.pipe(
            filter((v) => {
                return Math.abs(v) > this.options.tolerance;
            })
        );
        const shiftLenth$ = shift$.pipe(
            // eslint-disable-next-line rxjs/no-unsafe-takeuntil
            takeUntil(swipe$),
            endWith(0),
            repeat()
        );
        return { shift$: shiftLenth$, swipe$ };
    }
}
